import React from 'react';
import { Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { BasePDFDocument } from '@/shared/components/pdf/BasePDFDocument';
import { StoryboardFrame } from '@/features/storyboard/types/storyboard.types';
import { Shot } from '@/features/shotlist';

interface StoryboardPDFTemplateProps {
    projectTitle: string;
    sceneTitle?: string;
    frames: StoryboardFrame[];
    shots: Shot[];
    base64Images: Record<string, string>;
    brandLogoBase64?: string;
}

const styles = StyleSheet.create({
    page: { 
        paddingTop: 40,
        paddingBottom: 50,
        paddingLeft: 30,
        paddingRight: 30,
        backgroundColor: '#ffffff', 
        fontFamily: 'Helvetica' 
    },
    header: { 
        marginBottom: 20, 
        borderBottomWidth: 2, 
        borderBottomColor: '#000', 
        borderBottomStyle: 'solid',
        paddingBottom: 10, 
        textAlign: 'center' 
    },
    title: { 
        fontSize: 24, 
        fontWeight: 'bold', 
        textTransform: 'uppercase' 
    },
    sceneTitle: { 
        fontSize: 14, 
        color: '#4b5563', 
        marginTop: 4 
    },
    grid: { 
        flexDirection: 'row', 
        flexWrap: 'wrap', 
        justifyContent: 'space-between' 
    },
    card: { 
        width: '48%', 
        borderWidth: 1, 
        borderColor: '#1e293b', 
        borderStyle: 'solid',
        marginBottom: 20, 
        borderRadius: 4, 
        backgroundColor: '#f8fafc',
        display: 'flex',
        flexDirection: 'column'
    },
    imageContainer: { 
        width: '100%', 
        height: 180, 
        backgroundColor: '#ffffff', 
        borderBottomWidth: 1, 
        borderBottomColor: '#1e293b', 
        borderBottomStyle: 'solid',
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        padding: 4 
    },
    image: { 
        objectFit: 'contain', 
        width: '100%', 
        height: '100%' 
    },
    noImage: { 
        fontSize: 10, 
        color: '#94a3b8', 
        fontStyle: 'italic' 
    },
    details: { 
        padding: 10, 
        display: 'flex', 
        flexDirection: 'column' 
    },
    cardHeader: { 
        flexDirection: 'row', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1pt solid #cbd5e1', 
        paddingBottom: 5, 
        marginBottom: 6 
    },
    frameNumber: { 
        fontSize: 14, 
        fontWeight: 'bold' 
    },
    shotType: { 
        fontSize: 8, 
        backgroundColor: '#e2e8f0', 
        paddingHorizontal: 4,
        paddingVertical: 2, 
        borderRadius: 2, 
        textTransform: 'uppercase' 
    },
    section: { 
        marginBottom: 6 
    },
    label: { 
        fontSize: 7, 
        color: '#64748b', 
        textTransform: 'uppercase', 
        marginBottom: 2 
    },
    value: { 
        fontSize: 10, 
        color: '#0f172a',
        lineHeight: 1.3
    },
    valueItalic: { 
        fontSize: 10, 
        color: '#334155', 
        fontStyle: 'italic',
        lineHeight: 1.3
    },
    metaGrid: { 
        flexDirection: 'row', 
        flexWrap: 'wrap', 
        backgroundColor: '#f1f5f9', 
        padding: 6, 
        borderRadius: 2, 
        border: '1pt solid #e2e8f0', 
        marginTop: 4 
    },
    metaItem: { 
        width: '50%', 
        flexDirection: 'row', 
        justifyContent: 'space-between', 
        marginBottom: 3, 
        paddingRight: 6 
    },
    metaLabel: { 
        fontSize: 8, 
        color: '#64748b' 
    },
    metaValue: { 
        fontSize: 8, 
        color: '#334155',
        fontWeight: 'bold'
    },
    emptyState: { 
        textAlign: 'center', 
        marginTop: 40, 
        color: '#9ca3af', 
        fontStyle: 'italic', 
        fontSize: 14 
    }
});

export const StoryboardPDFTemplate: React.FC<StoryboardPDFTemplateProps> = ({
    projectTitle,
    sceneTitle,
    frames,
    shots,
    base64Images,
    brandLogoBase64
}) => {
    return (
        <BasePDFDocument brandLogoBase64={brandLogoBase64}>
            {/* Main Content Header */}
            <View style={styles.header} fixed>
                <Text style={styles.title}>{projectTitle}</Text>
                <Text style={styles.sceneTitle}>
                    {sceneTitle ? `Scene: ${sceneTitle}` : 'Full Project Storyboard'}
                </Text>
            </View>

            {frames.length === 0 ? (
                <Text style={styles.emptyState}>No frames to display.</Text>
            ) : (
                <View style={styles.grid}>
                    {frames.map((frame) => {
                        const shot = shots.find((s) => s.id === frame.shotId);
                        const imageSrc = base64Images[frame.id];

                        return (
                            <View key={frame.id} style={styles.card} wrap={false}>
                                <View style={styles.imageContainer}>
                                    {imageSrc ? (
                                        <Image src={imageSrc} style={styles.image} />
                                    ) : (
                                        <Text style={styles.noImage}>No Image</Text>
                                    )}
                                </View>

                                <View style={styles.details}>
                                    <View style={styles.cardHeader}>
                                        <Text style={styles.frameNumber}>Frame {frame.frameNumber}</Text>
                                        {shot?.shotType && (
                                            <Text style={styles.shotType}>{shot.shotType}</Text>
                                        )}
                                    </View>

                                    <View style={styles.section}>
                                        <Text style={styles.label}>Action / Description</Text>
                                        <Text style={frame.shotDescription ? styles.value : styles.valueItalic}>
                                            {frame.shotDescription || 'No action description'}
                                        </Text>
                                    </View>

                                    <View style={styles.section}>
                                        <Text style={styles.label}>Dialogue / Notes</Text>
                                        <Text style={frame.dialogueNotes ? styles.value : styles.valueItalic}>
                                            {frame.dialogueNotes || 'None'}
                                        </Text>
                                    </View>

                                    <View style={styles.metaGrid}>
                                        <View style={styles.metaItem}>
                                            <Text style={styles.metaLabel}>Angle:</Text>
                                            <Text style={styles.metaValue}>{shot?.cameraAngle || '-'}</Text>
                                        </View>
                                        <View style={styles.metaItem}>
                                            <Text style={styles.metaLabel}>Move:</Text>
                                            <Text style={styles.metaValue}>{shot?.cameraMovement || '-'}</Text>
                                        </View>
                                        <View style={styles.metaItem}>
                                            <Text style={styles.metaLabel}>Framing:</Text>
                                            <Text style={styles.metaValue}>{shot?.framing || '-'}</Text>
                                        </View>
                                        <View style={styles.metaItem}>
                                            <Text style={styles.metaLabel}>Duration:</Text>
                                            <Text style={styles.metaValue}>{shot?.duration || '-'}</Text>
                                        </View>
                                    </View>
                                </View>
                            </View>
                        );
                    })}
                </View>
            )}
        </BasePDFDocument>
    );
};
